import React from "react";
import { BUCKET_LABEL, finApi, money } from "@/lib/financeApi";
import { useFloatingWindows } from "@/lib/floatingWindows";
import { btnGold, ErrorBox, Loading, useLoad, when } from "@/components/nds/windows/finance/parts";
import { TxnCard, TxnFlags } from "@/components/nds/windows/finance/flow/TxnCard";
import { FixPanel } from "@/components/nds/windows/finance/flow/FixPanel";
import { PaycheckInline } from "@/components/nds/windows/finance/flow/PaycheckInline";

export const SOURCE_WINDOW = { paychecks: "fin_paychecks", penalties: "fin_penalties", rent_roll: "fin_rent_roll", program_fees: "fin_program_fees", savings_requests: "fin_savings",
  director_credits: "fin_director_credits", fund_requests: "fin_fund_requests", session_payouts: "fin_session_payouts", payroll: "fin_payroll", invoices: "fin_invoicing", clearinghouse: "fin_clearinghouse" };
const fmt = (k, v) => (typeof v === "number" ? money(v) : k === "split" ? Object.entries(v).map(([a, b]) => `${a} ${money(b)}`).join(" · ") : /_at$/.test(k) ? when(v) : String(v));

const Fact = ({ label, children, testid }) => (
  <div data-testid={testid}><div className="nds-label-dim text-[9px]">{label}</div><div className="text-sm text-nex-silver break-words">{children}</div></div>
);

/** Where a number came from (source record, chain of related rows) + the fixes. */
export function TxnDetail({ id, onOpen, onChanged }) {
  const [state, load] = useLoad(() => finApi.get(`/flow/txn/${id}`), [id]);
  const { openWindow } = useFloatingWindows();
  if (state.loading && !state.data) return <Loading testid="flow-txn-loading" />;
  if (state.error) return <ErrorBox testid="flow-txn-error" error={state.error} onRetry={load} />;
  const d = state.data;
  const t = d.transaction;
  const credit = t.direction === "credit";
  return (
    <div data-testid="flow-txn-detail" className="space-y-6">
      <div className="p-5 rounded-md border border-white/10 bg-white/[0.03] space-y-4">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex-1 min-w-[200px]"><div className="text-lg text-nex-silver font-semibold">{t.resident_name}</div><div className="text-sm text-nex-muted">{t.description || t.type_label}</div></div>
          <div data-testid="flow-txn-amount" className={`font-mono text-2xl tabular-nums ${t.voided ? "line-through text-nex-muted" : credit ? "text-emerald-300" : "text-red-300"}`}>{credit ? "+" : "−"}{money(t.amount).replace("−", "")}</div>
        </div>
        <TxnFlags t={t} />
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-4">
          <Fact label="Type">{t.type_label}</Fact><Fact label="Bucket">{BUCKET_LABEL[t.bucket] || t.bucket}</Fact>
          <Fact label="Posted">{when(t.created_at)}</Fact><Fact label="Bucket balance after">{money(t.balance_after)}</Fact>
          <Fact label="Entered by">{t.created_by?.name || "system"}</Fact>{t.reason && <Fact label="Reason">{t.reason}</Fact>}
          {t.reference_type && <Fact label="Source tag">{t.reference_type}</Fact>}
        </div>
      </div>
      <section className="space-y-2" data-testid="flow-txn-source">
        <h3 className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold">Where this came from</h3>
        {d.source ? (
          <div className="p-4 rounded-md border border-white/10 space-y-3">
            <div className="flex flex-wrap items-center gap-2"><span className="text-nex-silver font-medium">{d.source.label}</span>
              {SOURCE_WINDOW[d.source.collection] && <button type="button" onClick={() => openWindow(SOURCE_WINDOW[d.source.collection])} data-testid="flow-source-open" className={`${btnGold} ml-auto`}>Open {d.source.label}</button>}</div>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3">{Object.entries(d.source).filter(([k]) => !["collection", "label", "id"].includes(k)).map(([k, v]) => <Fact key={k} label={k.replace(/_/g, " ")}>{fmt(k, v)}</Fact>)}</div>
            {d.source.collection === "paychecks" && d.source.id && <PaycheckInline pid={d.source.id} onDone={() => { load(); onChanged(); }} />}
          </div>
        ) : <p className="text-sm text-nex-muted">{t.legacy_import ? "Imported from the old platform. There's no separate source record." : "Entered directly on the ledger."}</p>}
      </section>
      {!!d.related.length && (
        <section className="space-y-2" data-testid="flow-txn-related">
          <h3 className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold">Linked entries · {d.related.length}</h3>
          <div className="grid gap-2 sm:grid-cols-2">{d.related.map((x) => <TxnCard key={x.id} t={x} onOpen={onOpen} />)}</div>
        </section>
      )}
      <section className="space-y-2">
        <h3 className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold">Fix this entry</h3>
        <FixPanel d={d} onDone={() => { load(); onChanged(); }} />
      </section>
    </div>
  );
}
